<template>
  <div>
    <div>
      <v-breadcrumbs
        class="px-0"
        :items="[
          { title: 'Growth Record List', href: '/growth-record', color: 'primary' },
          { title: `${className} Growth Record` },
        ]"
      ></v-breadcrumbs>
    </div>
    <page-title :value="`${className} ${$t('growthRecord.title')}`" />
    <div class="my-4">
      <month-pager v-model="yearMonth"
        @input="pageClick"
      />
    </div>
    <div v-if="recordRequests.length > 0" class="py-2">
      <v-btn color="primary" variant="flat" :disabled="isSaving" @click="saveRecords">{{ $t('common_actions.save') }}</v-btn>
    </div>
    <div v-if="isSaving">
      <v-progress-linear indeterminate color="primary" />
    </div>
    <div class="children-table">
      <div class="children-table__headers">
        <div class="children-table__header col-name">{{ $t('growthRecord.childName') }}</div>
        <div class="children-table__header col-height">{{ $t('growthRecord.height') }}</div>
        <div class="children-table__header col-weight">{{ $t('growthRecord.weight') }}</div>
        <div class="children-table__header col-other">{{ $t('growthRecord.remarks') }}</div>
      </div>
      <class-growth-record-row
        v-for="r in records"
        :key="r.child.childId"
        :record="r"
        @inputHeight="inputHeight($event, r)"
        @inputWeight="inputWeight($event, r)"
        @inputRemarks="inputRemarks($event, r)"
      />
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';

import { classGrowthRecordModule, ChildGrowthRecordRequest } from '@/store/viewModules/growth-record/classGrowthRecordModule';

import PageTitle from '@/components/atoms/PageTitle.vue';
import ClassGrowthRecordRow from '@/components/organisms/growth-record/ClassGrowthRecrodRow.vue';
import MonthPager from '@/components/atoms/MonthPager.vue';
import dayjs from 'dayjs';

import { NurseryGrowthDetailResponse } from 'chaild-api/lib';
import LocalDataService from '@/service/LocalDataService';
import { classModule } from '@/store/dataModules/classModule';

interface DataType {
  isSaving: boolean;
}

export default defineComponent({
  components: {
    PageTitle,
    MonthPager,
    ClassGrowthRecordRow,
  },

  data: (): DataType => ({
    isSaving: false,
  }),

  computed: {
    // ⭕️ 修正2: yearMotnh のタイポを yearMonth に修正
    yearMonth: {
      get() {
        return classGrowthRecordModule.yearMonth;
      },
      set(value: string) {
        classGrowthRecordModule.setYearMonth(value);
        classGrowthRecordModule.listGrowthRecords();
      }
    },
    records() {
      return classGrowthRecordModule.growthRecords.filter((r) => {
        if (r.child.quitDate) {
          const now = dayjs();
          const quitDate = dayjs(r.child.quitDate);
          const diff = now.diff(quitDate);
          if (diff > 0) {
            return false;
          }
        }
        return true;
      });
    },
    recordRequests() {
      return classGrowthRecordModule.childGrowthRecordRequests;
    },
    className() {
      if (classGrowthRecordModule.classId) {
        const find = classModule.classes.find((c) => c.classId === classGrowthRecordModule.classId);
        if (find) {
          return find.className;
        }
      }
      return '';
    }
  },

  methods: {
    async pageClick(pageNumber: string) {
      if (pageNumber) sessionStorage.setItem('classGrowthRecordYearMonth', String(pageNumber));
    },
    inputHeight(value: number, record: NurseryGrowthDetailResponse) {
      const find = this.recordRequests.find((r) => r.childId === record.child.childId);
      const newRecord: ChildGrowthRecordRequest = find ? {...find} : { childId: record.child.childId };
      newRecord.height = value;

      classGrowthRecordModule.updateChildGrowthRecordRequests(newRecord);
    },
    inputWeight(value: number, record: NurseryGrowthDetailResponse) {
      const find = this.recordRequests.find((r) => r.childId === record.child.childId);
      const newRecord: ChildGrowthRecordRequest = find ? {...find} : { childId: record.child.childId };
      newRecord.weight = value;

      classGrowthRecordModule.updateChildGrowthRecordRequests(newRecord);
    },
    inputRemarks(value: string, record: NurseryGrowthDetailResponse) {
      const find = this.recordRequests.find((r) => r.childId === record.child.childId);
      const newRecord: ChildGrowthRecordRequest = find ? {...find} : { childId: record.child.childId };
      newRecord.remarks = value;

      classGrowthRecordModule.updateChildGrowthRecordRequests(newRecord);
    },
    async saveRecords() {
      this.isSaving = true;
      await classGrowthRecordModule.saveRecords();
      this.isSaving = false;
    }
  },

  mounted() {
    const classId = this.$route.params.classId as string;
    if (classId) {
      try {
        const classIdInt = parseInt(classId, 10);
        classGrowthRecordModule.setClassId(classIdInt);
        classGrowthRecordModule.listGrowthRecords();
      } catch {
        return;
      }
    }

    const nurseryId = LocalDataService.getNurseryId();
    if (nurseryId) {
      const classGrowthRecordYearMonth = sessionStorage.getItem('classGrowthRecordYearMonth');
      if (classGrowthRecordYearMonth) this.yearMonth = classGrowthRecordYearMonth;
      classModule.listClass({
        nurseryId: nurseryId,
      });
    }
  }
});
</script>

<style lang="scss" scoped>
.children-table__headers {
  display: flex;
  align-items: center;
  height: 30px;
  border-bottom: solid 1px #e0e0e0;
  margin-bottom: 8px;
}
.children-table__header {
  font-size: 12px;
  font-weight: bold;
}

::v-deep {
  .children-table__row {
    display: flex;
    align-items: center;
    height: 48px;
    padding: 8px 0;
  }
  .children-table__row-col {
    padding: 4px;
    font-size: 14px;
  }
  .col-name { width: 160px; }
  .col-height { width: 120px; }
  .col-weight { width: 120px; }
  .col-other { flex: 1; }
}
</style>